import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import styled, { css } from 'styled-components';
import SiteFooter from './marketing/SiteFooter';
import SiteHeader from './marketing/SiteHeader';
import SeoHead from './SeoHead';
import Loading from './shared/Loading';
import { white } from './theme/palette';

const Home = lazy(() =>
  import('./pages/home/Home').then((module) => ({ default: module.Home })),
);
const PricingPage = lazy(() => import('./pages/pricing/PricingPage'));
const Privacy = lazy(() => import('./pages/privacy/Privacy'));
const Terms = lazy(() => import('./pages/terms/Terms'));
const Cookie = lazy(() => import('./pages/cookie/Cookie'));
const Acceptable = lazy(() => import('./pages/acceptable/Acceptable'));
const Support = lazy(() => import('./pages/support/Support'));
const BlogIndex = lazy(() => import('./pages/blog/BlogIndex'));
const BlogPost = lazy(() => import('./pages/blog/BlogPost'));
const NotFound = lazy(() => import('./pages/NotFound'));

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${white};
`;

/**
 * Legal and support pages sit in a padded white reading column. The landing
 * page and the blog manage their own full-width sections instead — they run
 * edge-to-edge bands that the shared padding would break.
 */
const Main = styled.main<{ $fullBleed: boolean }>`
  flex: 1;
  min-height: calc(100vh - 64px);
  padding: ${({ $fullBleed }) => ($fullBleed ? '0' : '36px 40px 56px')};
  background: ${white};

  ${({ $fullBleed }) =>
    !$fullBleed &&
    css`
      > div {
        max-width: 1120px;
        margin: 0 auto;
      }
    `}

  @media (max-width: 768px) {
    padding: ${({ $fullBleed }) => ($fullBleed ? '0' : '22px 18px 40px')};
  }
`;

const trackMarketingEvent = (eventName: string) => {
  if (typeof window !== 'undefined') {
    (window as typeof window & {
      gtag?: (...args: unknown[]) => void;
    }).gtag?.('event', eventName);
  }
};

export function MarketingRouter() {
  const { pathname } = useLocation();
  const fullBleed =
    pathname === '/' || pathname === '/blog' || pathname.startsWith('/blog/');

  return (
    <>
      <SeoHead />
      <Shell>
        <SiteHeader onCtaClick={() => trackMarketingEvent('clicked_start_free')} />
        <Main $fullBleed={fullBleed}>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/support" element={<Support />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookie />} />
              <Route path="/acceptable-use" element={<Acceptable />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Main>
        <SiteFooter />
      </Shell>
    </>
  );
}

export default function PublicApp() {
  return (
    <BrowserRouter>
      <MarketingRouter />
    </BrowserRouter>
  );
}
